import { DUCT_TYPES, type DuctCalcType, type DuctLineUnit, type DuctProductProfile } from './ductwork';

/**
 * Báo giá nhanh (anh Tiến 09/10/2026): dòng BOQ ống gió ghi sẵn kích thước ("Co 90 độ 900×350", "Ống gió 800x400 L1200",
 * "Côn 800x400-600x300", "Co tròn D300") → tự nhận loại + kích thước để bộ tính CR-008 ra m² ngay, không phải mở hộp tính.
 *
 * Mặc định khi khách không ghi (hiện rõ trên dòng, NV sửa được):
 * - Vật liệu: ống gió trơn tôn 0,75 mm (mã OGV075); có "EI30/45/…" thì dùng mã ống gió EI tương ứng.
 * - Co vuông: R trong = W/2 (bán kính tâm = W, R/W = 1); co tròn: R tâm = D; góc 90°.
 * - Ống thẳng: ĐVT khách là m/md → tính theo mét dài; không ghi dài → 1,2 m/ống.
 * - Côn (bầu giảm): dài 300 mm.
 * Không nhận: ống gió mềm, vuông tròn thiếu đường kính (xem ductPrefill), dòng ĐVT m² (khách đã ghi diện tích).
 */
export interface ParsedDuct {
  calcType: DuctCalcType;
  family: 'rect' | 'round';
  /** Kích thước theo mm (ống thẳng: L theo m, đúng đơn vị trường của bộ tính) */
  dimensions: Record<string, number>;
  pieces: number;
  lineUnit: DuctLineUnit;
  /** Cấp chống cháy khách ghi (EI30 → 30) */
  ei: number | null;
  /** Độ dày tôn khách ghi (mm) */
  thicknessMm: number | null;
  /** Mô tả ngắn: "Co vuông 900×350 · R 450 · 90°" */
  text: string;
  /** Các giá trị em tự lấy mặc định (NV kiểm lại) */
  assumed: string[];
}

export const DUCT_DEFAULT_THICKNESS_MM = 0.75;
export const DUCT_DEFAULT_STRAIGHT_LENGTH_M = 1.2;
export const DUCT_DEFAULT_REDUCER_LENGTH_MM = 300;

/** Bỏ dấu, chữ thường, "×"/"*" → "x" */
export function ductNorm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[×✕*]/g, 'x')
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\s+/g, ' ')
    .trim();
}

const num = (s: string) => Number(s.replace(',', '.'));
const fmt = (n: number) => n.toLocaleString('vi-VN', { maximumFractionDigits: 3 });

// Cạnh ống gió: 80–3000 mm (loại "ống thép 21x1.6", "hộp 50x100x2")
const SIDE = '(\\d{2,4})';
const PAIR = new RegExp(`(?<![\\d.])${SIDE}\\s*x\\s*${SIDE}(?![\\d.]|\\s*x\\s*\\d)`, 'g');
const okSide = (n: number) => n >= 80 && n <= 3000;

const KIND = {
  tee: /\b(te|tee|chac 3|ba chac)\b/,
  transition: /\b(vuong tron|chuyen tiep|transition)\b/,
  offset: /\b(luon|offset)\b/,
  elbow: /\b(co|cut|elbow|bend)\b/,
  reducer: /\b(con|reducer|bau giam)\b/,
  cap: /\b(bit|bit dau|end cap|cap)\b/,
  straight: /\b(ong|duct)\b/,
  skip: /\b(chu y|chac y|y|mem|flex|giam am|tieu am|mieng|cua gio|louver|van|damper|quat|hop gio|plenum|take ?off|got giay)\b/,
};
// Từ khóa ống nước / thép: "co hàn DN100", "ống thép hộp 100x100"… chỉ nhận khi dòng/nhóm có chữ "gió"
const NOT_AIR = /\b(thep|hop|inox|ppr|hdpe|upvc|pvc|uPVC|ren|han|ma kem|den|dn\s?\d+|phi|nhua|dong|sch)\b/;

export interface DuctParseInput {
  name: string;
  spec?: string | null;
  unit?: string | null;
  quantity?: number | null;
  /** Tiêu đề nhóm phía trên ("HỆ THỐNG ỐNG GIÓ") */
  group?: string | null;
}

/** Mặc định tê: ống chính dài = cạnh nhánh + 200 mm; nhánh dài 200 mm. Lượn: dài 600 mm, lệch 200 mm. Vuông tròn: dài 300 mm. */
export const DUCT_DEFAULT_BRANCH_MM = 200;
export const DUCT_DEFAULT_OFFSET = { L: 600, e: 200 } as const;
export const DUCT_DEFAULT_TRANSITION_LENGTH_MM = 300;

export function parseDuctLine(row: DuctParseInput): ParsedDuct | null {
  const t = ductNorm([row.name, row.spec].filter(Boolean).join(' '));
  const unit = ductNorm(row.unit ?? '');
  if (/^(m2|m²|met vuong)$/.test(unit) || /\bm2\b|m²/.test(unit)) return null;
  // "vuông tròn 1400×300/D quạt": đầu tròn theo cổ quạt, không phải dòng quạt
  if (KIND.skip.test(t.replace(/\bd\s?quat\b/g, ''))) return null;
  const air = /\bgio\b|\bduct\b|\bei\s?\d{2,3}\b/.test(t) || /\bgio\b|\bduct\b|\bhvac\b|\bdhkk\b|dieu hoa/.test(ductNorm(row.group ?? ''));
  if (!air && NOT_AIR.test(t)) return null;

  const eiM = t.match(/\bei\s?(\d{2,3})\b/);
  const ei = eiM ? Number(eiM[1]) : null;
  const thM = t.match(/\b(?:ton|day|tole|t\s?=)\s*(\d(?:\.\d{1,2})?)\s*(?:mm|ly|li|zem)?\b/) ?? t.match(/(?<![\d.x])(0\.\d{1,2}|1\.[0-5]\d?)\s*(?:mm|ly|li|zem)\b/);
  const thicknessMm = thM ? num(thM[1]!) : null;
  const pieces = row.quantity && row.quantity > 0 ? row.quantity : 1;
  const assumed: string[] = [];

  const pairs = [...t.matchAll(PAIR)].map((m) => [num(m[1]!), num(m[2]!)] as [number, number]).filter(([a, b]) => okSide(a) && okSide(b));
  // Đường kính: "D300", "Ø300", "phi 300"; số liền sau bằng / hoặc - ("D400/300", "D400-D300") là đường kính thứ 2
  const diams: number[] = [];
  for (const m of t.matchAll(/(?:\b(?:d|phi)\s?|ø\s?)(\d{2,4})\b(?:\s*[/-]\s*(?:d|phi|ø)?\s*(\d{2,4})\b)?/g)) {
    for (const v of [m[1], m[2]]) if (v && okSide(num(v))) diams.push(num(v));
  }
  const D = diams[0] ?? null;
  const angleM = t.match(/\b(\d{2,3})\s*(?:do|°|deg)\b/) ?? t.match(/(\d{2,3})°/);
  const angle = angleM ? num(angleM[1]!) : null;
  const rM = t.match(/\br\s?=?\s?(\d{1,4})\b/);
  const lenM = t.match(/\b(?:l|dai)\s?=?\s?(\d+(?:\.\d+)?)\s*(mm|m)?\b/);
  const lengthMm = lenM ? (lenM[2] === 'm' || (!lenM[2] && num(lenM[1]!) < 20) ? num(lenM[1]!) * 1000 : num(lenM[1]!)) : null;
  const offM = t.match(/\b(?:lech|e)\s?=?\s?(\d{1,4})\s*(?:mm)?\b/);
  const offsetMm = offM ? num(offM[1]!) : null;

  // "có bảo ôn", "có cách nhiệt"… (BOQ dự toán 26/10): chữ "có" bỏ dấu thành "co", không phải co (cút)
  const k = t.replace(/\bco (?=bao on|cach nhiet|lop|kem|gioang|mat bich|son|phu kien|treo|ty ren|bu ?long|ke\b|gia do)/g, '');
  const kind = KIND.tee.test(k)
    ? 'tee'
    : KIND.transition.test(k)
      ? 'transition'
      : KIND.offset.test(k)
        ? 'offset'
        : KIND.elbow.test(k)
          ? 'elbow'
          : KIND.reducer.test(k)
            ? 'reducer'
            : KIND.cap.test(k)
              ? 'cap'
              : KIND.straight.test(k) && air
                ? 'straight'
                : null;
  if (!kind) return null;
  const base = { pieces, ei, thicknessMm, assumed };
  const angleOr90 = () => {
    if (angle && angle > 0 && angle <= 180) return angle;
    assumed.push('góc 90°');
    return 90;
  };
  const lenOr = (def: number, label: string) => {
    if (lengthMm !== null) return lengthMm;
    assumed.push(`${label} ${fmt(def)} mm`);
    return def;
  };
  const piece = (calcType: DuctCalcType, family: 'rect' | 'round', dimensions: Record<string, number>, text: string): ParsedDuct => ({
    ...base,
    calcType,
    family,
    dimensions,
    lineUnit: 'PIECE',
    text,
  });

  if (kind === 'tee' && pairs[0]) {
    const [W, H] = pairs[0];
    // Nhánh ghi cỡ thứ 2 ("Tê 800x400/400x300"); không ghi = nhánh cùng cỡ ống chính
    const [Wb, hb] = pairs[1] ?? [W, H];
    if (!pairs[1]) assumed.push('nhánh cùng cỡ ống chính');
    const Hb = Math.min(hb, W);
    const Lm = Wb + DUCT_DEFAULT_BRANCH_MM;
    const Lb = lengthMm ?? DUCT_DEFAULT_BRANCH_MM;
    assumed.push(`ống chính dài ${fmt(Lm)} mm`);
    if (lengthMm === null) assumed.push(`nhánh dài ${fmt(Lb)} mm`);
    return piece('rect_tee', 'rect', { W, H, Lm, Wb, Hb, Lb }, `Tê ${W}×${H} → ${Wb}×${Hb}`);
  }
  if (kind === 'tee' && D) {
    const Db = Math.min(diams[1] ?? D, D);
    if (diams[1] === undefined) assumed.push('nhánh cùng cỡ ống chính');
    const Lm = Db + DUCT_DEFAULT_BRANCH_MM;
    const Lb = lengthMm ?? DUCT_DEFAULT_BRANCH_MM;
    assumed.push(`ống chính dài ${fmt(Lm)} mm`);
    if (lengthMm === null) assumed.push(`nhánh dài ${fmt(Lb)} mm`);
    return piece('round_tee', 'round', { Dm: D, Lm, Db, Lb }, `Tê tròn D${D} → D${Db}`);
  }
  if (kind === 'transition' && pairs[0] && D) {
    const [W, H] = pairs[0];
    const L = lenOr(DUCT_DEFAULT_TRANSITION_LENGTH_MM, 'dài');
    return piece('rect_to_round', 'rect', { W, H, D, L }, `Vuông tròn ${W}×${H} → D${D} · L ${fmt(L)}`);
  }
  if (kind === 'offset' && pairs[0]) {
    const [W, H] = pairs[0];
    const L = lenOr(DUCT_DEFAULT_OFFSET.L, 'dài lượn');
    let e = offsetMm;
    if (e === null) {
      e = DUCT_DEFAULT_OFFSET.e;
      assumed.push(`lệch ${e} mm`);
    }
    return piece('rect_offset', 'rect', { W, H, L, e }, `Lượn ${W}×${H} · L ${fmt(L)} · lệch ${fmt(e)}`);
  }
  if (kind === 'elbow' && pairs[0]) {
    const [W, H] = pairs[0];
    const ang = angleOr90();
    let R = rM ? num(rM[1]!) : null;
    if (R === null) {
      R = Math.round(W / 2);
      assumed.push(`R trong = W/2 = ${fmt(R)}`);
    }
    return piece('rect_elbow', 'rect', { W, H, R, angle: ang }, `Co vuông ${W}×${H} · R ${fmt(R)} · ${fmt(ang)}°`);
  }
  if (kind === 'elbow' && D) {
    const ang = angleOr90();
    let R = rM ? num(rM[1]!) : null;
    if (R === null || R < D / 2) {
      R = D;
      assumed.push(`R tâm = D = ${fmt(R)}`);
    }
    return piece('round_elbow', 'round', { D, R, angle: ang }, `Co tròn D${D} · R ${fmt(R)} · ${fmt(ang)}°`);
  }
  if (kind === 'reducer' && pairs.length >= 2) {
    const [[W1, H1], [W2, H2]] = pairs as [[number, number], [number, number]];
    const L = lenOr(DUCT_DEFAULT_REDUCER_LENGTH_MM, 'dài côn');
    return piece('rect_reducer', 'rect', { W1, H1, W2, H2, L }, `Côn ${W1}×${H1} → ${W2}×${H2} · L ${fmt(L)}`);
  }
  if (kind === 'reducer' && pairs[0] && D) {
    // "Côn 600x300 - D300" = vuông tròn
    const [W, H] = pairs[0];
    const L = lenOr(DUCT_DEFAULT_TRANSITION_LENGTH_MM, 'dài');
    return piece('rect_to_round', 'rect', { W, H, D, L }, `Vuông tròn ${W}×${H} → D${D} · L ${fmt(L)}`);
  }
  if (kind === 'reducer' && diams.length >= 2) {
    const D1 = Math.max(diams[0]!, diams[1]!);
    const D2 = Math.min(diams[0]!, diams[1]!);
    const L = lenOr(DUCT_DEFAULT_REDUCER_LENGTH_MM, 'dài côn');
    return piece('round_reducer', 'round', { D1, D2, L }, `Côn tròn D${D1} → D${D2} · L ${fmt(L)}`);
  }
  if (kind === 'cap' && pairs[0]) {
    const [W, H] = pairs[0];
    return piece('rect_end_cap', 'rect', { W, H }, `Bịt đầu ${W}×${H}`);
  }
  if (kind === 'cap' && D) return piece('round_end_cap', 'round', { D }, `Bịt đầu tròn D${D}`);
  if (kind === 'straight' && (pairs[0] || D)) {
    const meters = /^(m|md|met|met dai|m dai)$/.test(unit);
    let L: number;
    let lineUnit: DuctLineUnit;
    let p = pieces;
    if (meters) {
      // Khách ghi tổng mét dài: 1 "cái" dài đúng số mét, bán theo Mét
      L = pieces;
      p = 1;
      lineUnit = 'METER';
    } else {
      L = lengthMm ? lengthMm / 1000 : DUCT_DEFAULT_STRAIGHT_LENGTH_M;
      if (!lengthMm) assumed.push(`dài ${fmt(L)} m/ống`);
      lineUnit = 'PIPE';
    }
    if (pairs[0]) {
      const [W, H] = pairs[0];
      return { ...base, pieces: p, calcType: 'rect_straight', family: 'rect', dimensions: { W, H, L }, lineUnit, text: `Ống vuông ${W}×${H} · dài ${fmt(L)} m` };
    }
    return { ...base, pieces: p, calcType: 'round_straight', family: 'round', dimensions: { D: D!, L }, lineUnit, text: `Ống tròn D${D} · dài ${fmt(L)} m` };
  }
  return null;
}

/**
 * Dòng chưa đủ kích thước để tính nhưng biết loại (BOQ dự toán 27/10: "Lắp đặt chuyển vuông tròn 1400×300/D quạt"):
 * trả loại + kích thước đã đọc để người lập chỉ nhập phần thiếu (đường kính đầu tròn).
 */
export function ductPrefill(row: DuctParseInput): { calcType: DuctCalcType; dimensions: Record<string, number>; missing: string } | null {
  const t = ductNorm([row.name, row.spec].filter(Boolean).join(' '));
  if (!KIND.transition.test(t)) return null;
  const pair = [...t.matchAll(PAIR)].map((m) => [num(m[1]!), num(m[2]!)]).find(([a, b]) => okSide(a!) && okSide(b!));
  if (!pair) return null;
  const lenM = t.match(/\b(?:l|dai)\s?=?\s?(\d{2,4})\s*(?:mm)?\b/);
  return {
    calcType: 'rect_to_round',
    dimensions: { W: pair[0]!, H: pair[1]!, L: lenM ? num(lenM[1]!) : DUCT_DEFAULT_TRANSITION_LENGTH_MM },
    missing: 'đường kính đầu tròn D (vd cổ quạt)',
  };
}

/** Van gió (FD, VCD, OBD, MFD…), louver, miệng gió: diện tích mặt W×H (m²) để tính giá theo đ/m² mặt; null = không phải / không có cỡ */
export function damperFaceM2(row: DuctParseInput): { faceM2: number; text: string } | null {
  const t = ductNorm([row.name, row.spec].filter(Boolean).join(' '));
  if (!/\b(van gio|van chan lua|van (?:fd|fsd|vcd|obd|mfd|sd)|fd|fsd|vcd|obd|mfd|damper|louver|mieng gio|cua gio)\b/.test(t)) return null;
  const pair = [...t.matchAll(PAIR)].map((m) => [num(m[1]!), num(m[2]!)]).find(([a, b]) => okSide(a!) && okSide(b!));
  if (pair) return { faceM2: (pair[0]! * pair[1]!) / 1e6, text: `Mặt ${pair[0]}×${pair[1]}` };
  const d = t.match(/(?:\b(?:d|phi)\s?|ø\s?)(\d{2,4})\b/);
  if (d && okSide(num(d[1]!))) return { faceM2: (Math.PI * num(d[1]!) ** 2) / 4e6, text: `Mặt D${d[1]}` };
  return null;
}

/** Mã ống gió EI: "Ống Gió EI30-45 (0.75mm)" → [30, 45]; "OGEI120" → [120, 120] */
export function eiRangeOf(name: string, code = ''): [number, number] | null {
  const m = ductNorm(name).match(/\bei\s?(\d{2,3})(?:\s?[-/]\s?(?:ei\s?)?(\d{2,3}))?/) ?? ductNorm(code).match(/ei(\d{2,3})(?:-(\d{2,3}))?/);
  if (!m) return null;
  const a = Number(m[1]);
  const b = m[2] ? Number(m[2]) : a;
  return [Math.min(a, b), Math.max(a, b)];
}

export interface DuctProductOption {
  id: number;
  code: string;
  name: string;
  profile: DuctProductProfile;
}

/**
 * Chọn mã cho dòng ống gió đã nhận dạng: EI → mã EI có dải cấp bao cấp khách ghi; thường → mã không EI, đúng họ
 * (vuông/tròn, mã "any" dùng được cả 2), đúng độ dày (khách không ghi = 0,75 mm), ưu tiên tôn mạ kẽm, rồi mã OGV075.
 * null = danh mục không có mã phù hợp (để NV chọn tay).
 */
export function pickDuctProduct(d: Pick<ParsedDuct, 'family' | 'ei' | 'thicknessMm'>, list: DuctProductOption[]): DuctProductOption | null {
  const fam = list.filter((p) => p.profile.family === d.family || p.profile.family === 'any' || (d.family === 'rect' && p.profile.family === 'rect'));
  if (d.ei) {
    const ei = fam.filter((p) => p.profile.ei).filter((p) => {
      const r = eiRangeOf(p.name, p.code);
      return !!r && d.ei! >= r[0] && d.ei! <= r[1];
    });
    return pickThickness(ei, d.thicknessMm, true);
  }
  return pickThickness(
    fam.filter((p) => !p.profile.ei),
    d.thicknessMm ?? DUCT_DEFAULT_THICKNESS_MM,
    false,
  );
}

function pickThickness(list: DuctProductOption[], t: number | null, loose: boolean): DuctProductOption | null {
  const same = t == null ? list : list.filter((p) => p.profile.thicknessMm != null && Math.abs(p.profile.thicknessMm - t) < 1e-6);
  const pool = same.length ? same : loose ? list : [];
  const score = (p: DuctProductOption) => (p.profile.materialCode === 'GI' ? 2 : 0) + (p.profile.family !== 'any' ? 1 : 0) + (/^OGV075$/i.test(p.code) ? 4 : 0);
  return [...pool].sort((a, b) => score(b) - score(a) || a.code.localeCompare(b.code))[0] ?? null;
}

/** Dòng nhận dạng được có tính được với mã đã chọn không (đúng họ loại tính). */
export function ductFitsProduct(d: Pick<ParsedDuct, 'calcType'>, profile: DuctProductProfile | null | undefined): boolean {
  if (!profile || profile.family === 'piece') return false;
  const f = DUCT_TYPES[d.calcType].family;
  return profile.family === 'any' || profile.family === f || (d.calcType === 'rect_to_round' && profile.family === 'round');
}

/**
 * Khóa "học theo mẫu tên" của dòng ống gió (anh Tiến 09/10/2026): kích thước, góc, loại (co/côn/ống) KHÔNG làm đổi mã
 * (1 mã cho mọi hình dạng, CR-008), chỉ cấp EI, độ dày và vật liệu khách ghi mới đổi mã. Đổi tay 1 dòng
 * "Co 90 độ 800×350 EI60" sang mã X → mọi dòng ống gió ghi EI60 (mọi kích thước) sau này tự ra mã X.
 */
export function ductLearnKey(d: Pick<ParsedDuct, 'ei' | 'thicknessMm'>, text: string): string {
  const t = ductNorm(text);
  const mat = /\b316\b/.test(t) ? 'ss316' : /\binox|\bsus\b|\b304\b/.test(t) ? 'ss304' : /\bnhom\b|\balu/.test(t) ? 'al' : /\bden\b/.test(t) ? 'den' : '';
  return `duct:ei${d.ei ?? 0}|t${d.thicknessMm ?? ''}|${mat}`;
}
